import { CalendarDaysIcon, ClockIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import type { KeyboardEvent } from "react";
import { useRef } from "react";

export type CreateEntryTab = "timeTracking" | "absence";

type Props = {
	tab: CreateEntryTab;
	onChange: (tab: CreateEntryTab) => void;
	/** A locked tab stays clickable: its panel shows the reason of the lock. */
	lockedTab?: CreateEntryTab;
};

/**
 * The absence icon is a calendar, against the clock of the trackings: an absence is booked in whole days and a
 * tracking in hours.
 */
const tabs = [
	{ tab: "timeTracking", label: "Zeiterfassung", Icon: ClockIcon },
	{ tab: "absence", label: "Abwesenheit", Icon: CalendarDaysIcon },
] as const;

/**
 * Two quiet text tabs above the form. They are not a prominent switch: the time tracking form is the default.
 *
 * A day without tracking opens the sidebar on "Abwesenheit", because an absence can still be booked on it. The
 * "Zeiterfassung" tab then shows "gesperrt". A click on it shows the reason in the tab, not in a tooltip: a
 * tooltip is not reachable on a phone, and a disabled tab would hide the reason from a keyboard user.
 *
 * The gap between the tabs is small on purpose: at 375 px the pill pushes the second tab against the close button of
 * the sidebar, which sits at the right edge.
 */
export function CreateEntryTabs({ tab, onChange, lockedTab }: Props) {
	const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

	const handleKeyDown = (event: KeyboardEvent, index: number) => {
		if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
		event.preventDefault();
		const nextIndex = (index + (event.key === "ArrowRight" ? 1 : tabs.length - 1)) % tabs.length;
		onChange(tabs[nextIndex].tab);
		buttonRefs.current[nextIndex]?.focus();
	};

	return (
		<div role="tablist"
			 aria-label="Anlegen"
			 className="flex gap-2 border-b border-gray-200 px-7 pt-4">
			{tabs.map(({ tab: optionTab, label, Icon }, index) => {
				const isActive = tab === optionTab;
				return (
					<button key={optionTab}
							ref={(element) => {
								buttonRefs.current[index] = element;
							}}
							type="button"
							role="tab"
							aria-selected={isActive}
							tabIndex={isActive ? 0 : -1}
							onClick={() => onChange(optionTab)}
							onKeyDown={(event) => handleKeyDown(event, index)}
							className={clsx("-mb-px flex items-center gap-1.5 border-b-2 pb-2 text-sm font-medium",
								isActive ? "border-primary-500 text-gray-900" : "border-transparent text-gray-500 hover:text-gray-700")}>
						<Icon className="size-4"
							  aria-hidden="true" />
						{label}
						{optionTab === lockedTab && (
							<span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-normal text-gray-500">gesperrt</span>
						)}
					</button>
				);
			})}
		</div>
	);
}
